import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { createTopUp, formatCreditsAsRand } from '../services/walletService';
import { useWallet } from '../context/WalletContext';
import { COBALT, COBALT_TINT, INK, FONT_MONO, moneySx } from '../theme/kiff';

// Preset amounts in credits (1 credit = R1.00), each anchored to what it
// actually buys so the numbers mean something. Walks start at R100.
const PRESETS = [
  { amount: 100, note: '≈ 1 walk' },
  { amount: 200, note: '≈ 2 walks' },
  { amount: 500, note: '≈ a week of walks' },
];

export const MIN_TOPUP = 50;
export const MAX_TOPUP = 10000;

/**
 * Load credits onto the wallet. Redirects to Stitch to collect payment.
 *
 * Pass `settleChargeId` to have the credits immediately pay that charge once
 * they land — this is what makes the booking checkout a single payment rather
 * than "top up, come back, pay again".
 */
const TopUpDialog = ({
  open,
  onClose,
  defaultAmount,
  settleChargeId = null,
  title = 'Load credits',
  helperText,
  returnUrl,
  cancelUrl,
}) => {
  const { balance } = useWallet();
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      // Round the suggested amount up to the minimum top-up.
      const suggested = Math.max(Number(defaultAmount) || 250, MIN_TOPUP);
      setAmount(String(Math.ceil(suggested)));
      setError(null);
    }
  }, [open, defaultAmount]);

  const parsedAmount = Number(amount);
  const amountIsValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount >= MIN_TOPUP &&
    parsedAmount <= MAX_TOPUP;

  const handleSubmit = async () => {
    if (!amountIsValid) return;

    try {
      setSubmitting(true);
      setError(null);

      const result = await createTopUp(parsedAmount, {
        settleChargeId,
        returnUrl,
        cancelUrl,
      });

      if (!result?.payment_url) {
        throw new Error('Could not start the top-up. Please try again.');
      }

      // PaymentReturn reads this to verify the payment after Stitch redirects back
      localStorage.setItem('pending_charge_id', String(result.charge_id));
      window.location.href = result.payment_url;
    } catch (err) {
      console.error('Top-up failed:', err);
      setError(err.message || 'Could not start the top-up. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText ||
            "You'll be redirected to Stitch to pay. Credits appear in your wallet as soon as the payment clears."}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Current balance: {formatCreditsAsRand(balance)}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          {PRESETS.map(({ amount: value, note }) => {
            const selected = parsedAmount === value;
            return (
              <ButtonBase
                key={value}
                onClick={() => setAmount(String(value))}
                sx={{
                  flex: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  p: 1.25,
                  border: `2px solid ${selected ? COBALT : INK}`,
                  borderRadius: '10px',
                  backgroundColor: selected ? COBALT_TINT : 'transparent',
                  textAlign: 'left',
                }}
              >
                <Typography sx={{ ...moneySx, fontSize: 15 }}>R{value}</Typography>
                <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {note}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        <TextField
          fullWidth
          autoFocus
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          inputProps={{ min: MIN_TOPUP, max: MAX_TOPUP, step: '1' }}
          InputProps={{
            startAdornment: <InputAdornment position="start">R</InputAdornment>,
          }}
          error={amount !== '' && !amountIsValid}
          helperText={
            amount !== '' && !amountIsValid
              ? `Enter an amount between R${MIN_TOPUP} and R${MAX_TOPUP}.`
              : `Any amount from R${MIN_TOPUP}. You'll receive ${
                  amountIsValid ? parsedAmount.toFixed(2) : '0.00'
                } credits.`
          }
        />

        <Typography
          sx={{ fontFamily: FONT_MONO, fontSize: 11, color: 'text.disabled', mt: 2, textAlign: 'center' }}
        >
          Secured by Stitch · instant EFT or card
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!amountIsValid || submitting}
          startIcon={submitting ? <CircularProgress size={18} /> : null}
        >
          {submitting ? 'Redirecting...' : 'Continue to payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopUpDialog;
